<template>
    <!--
        THE ADMIN SHELL'S LEFT COLUMN: a brand row, the grouped menu, and the signed-in user at
        the bottom — the three things a console's sidebar is for, in the order they are looked at.

        It paints `--ui-bg-chrome` — the project surface `theme/theme.css` declares and
        the house theme separates from the canvas. THE FRAME IS ONE SURFACE, and in
        this theme that surface is the PANEL colour in both modes: white in light, and in dark
        the same mix the cards take, both standing on a darker canvas. The hairline on the
        right edge is the seam between the frame and the page. A theme that says nothing leaves
        the variable at `--ui-bg` and gets the flat single-surface shell instead.

        Opaque, not a tint over the canvas: on mobile this column is the overlay.

        FLEET: on the desk it can be FOLDED down to its icons (`folded`, `w-16`) — a recorded
        deviation (CLAUDE.md): this console's home is a map, and a map wants the width.
        Folded, every row keeps its icon, its tooltip and a dot for its badge; unfolded it is
        the template's `w-64` exactly. The drawer is never folded.
    -->
    <nav
        class="flex flex-col shrink-0 border-r border-default bg-(--ui-bg-chrome)"
        :class="folded ? 'w-16' : 'w-64'"
    >
        <div
            class="h-(--ui-header-height) shrink-0 flex items-center gap-2.5 border-b border-default"
            :class="folded ? 'justify-center px-2' : 'px-4'"
        >
            <!-- FLEET: the brand row is the way home — the map. The mark is the template's
                 `public/logo.svg`, which is the same drawing as the favicon. -->
            <RouterLink
                :to="{ name: 'map' }"
                class="flex items-center gap-2.5 min-w-0 flex-1"
                :class="{ 'justify-center': folded }"
                :aria-label="$t('brand.product_name')"
                @click="closeMenu"
            >
                <img src="/logo.svg" alt="" class="size-6 shrink-0" >
                <div v-if="!folded" class="leading-tight min-w-0 flex-1">
                    <p class="text-sm font-semibold text-highlighted truncate">{{ $t('brand.product_name') }}</p>
                    <p class="font-mono text-[10px] uppercase tracking-widest text-dimmed truncate">{{ $t('brand.tagline') }}</p>
                </div>
            </RouterLink>

            <!--
                THE WAY OUT, on mobile only. Open, this column IS the overlay and it covers the
                topbar's hamburger, so the affordance that opened the menu cannot also close it.
                The X lives where the eye already is — in the brand row, opposite the mark.
            -->
            <UButton
                v-if="isMobile"
                color="neutral"
                variant="ghost"
                icon="i-lucide-x"
                :aria-label="$t('layout.close_menu')"
                class="-mr-1 shrink-0"
                @click="closeMenu"
            />
        </div>

        <div class="flex flex-col gap-5 flex-1 overflow-y-auto py-4" :class="folded ? 'px-2' : 'px-3'">
            <div v-for="(group, index) in groups" :key="`group-${index}`" class="flex flex-col gap-1">
                <!-- FLEET: folded, a group header becomes a rule — the grouping survives, the
                     word does not fit. -->
                <template v-if="group.labelKey">
                    <USeparator v-if="folded" class="pb-1" />
                    <p
                        v-else
                        class="px-2.5 pb-1 font-mono text-[11px] uppercase tracking-widest text-muted"
                    >{{ $t(group.labelKey) }}</p>
                </template>

                <UNavigationMenu
                    orientation="vertical"
                    :items="group.items"
                    :collapsed="folded"
                    tooltip
                    :ui="{ link: folded ? 'py-2 justify-center' : 'py-2' }"
                />
            </div>

            <!-- FLEET: folding belongs to the desk; in the drawer the menu is out of the way
                 the moment something is picked. -->
            <UButton
                v-if="!isMobile"
                class="mt-auto"
                :class="folded ? 'self-center' : 'self-start'"
                :icon="folded ? 'i-lucide-panel-left-open' : 'i-lucide-panel-left-close'"
                :label="folded ? undefined : $t('nav.collapse')"
                color="neutral"
                variant="ghost"
                size="sm"
                :aria-label="folded ? $t('nav.expand') : $t('nav.collapse')"
                :aria-expanded="!folded"
                @click="toggleCollapsed"
            />
        </div>

        <!--
            The signed-in user, where an admin console puts it. It carries the sign-out action
            too — one deliberate place for it, so the topbar does not repeat the same menu.
        -->
        <div class="shrink-0 border-t border-default" :class="folded ? 'p-2' : 'p-3'">
            <UDropdownMenu :items="userMenu" :content="{ align: 'start', side: 'top' }" class="w-full">
                <button
                    type="button"
                    class="w-full flex items-center gap-2.5 rounded-md px-2 py-2 text-left hover:bg-elevated transition-colors"
                    :class="{ 'justify-center': folded }"
                    :aria-label="folded ? userName : undefined"
                >
                    <span class="flex size-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary text-xs font-semibold">
                        {{ initials }}
                    </span>

                    <template v-if="!folded">
                        <span class="min-w-0 flex-1 leading-tight">
                            <span class="block truncate text-sm font-medium text-highlighted">{{ userName }}</span>
                            <span class="block truncate font-mono text-[10px] uppercase tracking-widest text-muted">{{ userRole }}</span>
                        </span>

                        <UIcon name="i-lucide-chevrons-up-down" class="size-4 shrink-0 text-dimmed" />
                    </template>
                </button>
            </UDropdownMenu>

            <!--
                FLEET: what is running, where somebody reporting a problem can read it off: the
                console's own version, and the API's as the API reports it. They are two
                projects with two versions, and after a deploy of one of them they differ. The
                commits are in the hover title. Folded there is no room for it.
            -->
            <div v-if="!folded" class="px-2 pt-2 text-xs text-dimmed" :title="buildTitle">
                <div>{{ $t('nav.version_web', { version: webVersion }) }}</div>
                <div v-if="api">{{ $t('nav.version_api', { version: api.version }) }}</div>
            </div>
        </div>
    </nav>
</template>

<script lang="ts" setup>
    import type { NavigationMenuItem } from '@nuxt/ui'
    import { computed, onMounted, ref } from 'vue'
    import { useI18n } from 'vue-i18n'
    import { RouterLink, useRoute, useRouter } from 'vue-router'

    import { NAV_GROUPS, useLayout, type NavBadgeSource } from '@/layouts/layout'
    import { roleLabel } from '@/libs/user'
    import { useAlertsStore } from '@/stores/alerts'
    import { useAppStore } from '@/stores/app'
    import { useFetchStore } from '@/stores/fetch'
    import { useRemindersStore } from '@/stores/reminders'
    import type { IVersion } from '@/types/Common'

    /**
     * The administration menu. The MODEL lives in `layouts/layout.ts` (`NAV_GROUPS`), because
     * the topbar titles the page from the same list — one list, so a section can never be named
     * one thing in the menu and another in the title bar.
     *
     * It is a `UNavigationMenu orientation="vertical"` per group, not one menu with `type:
     * 'label'` rows: a group is a separate list with its own header, and rendering the header
     * ourselves is what lets it be the small mono uppercase signpost the house theme asks for
     * rather than the component's own bold label slot.
     *
     * THE ACTIVE ROW IS A SOFT PRIMARY PLATE. The component's `pill` variant already colours an
     * active row's label AND its leading icon `text-primary` (its `color="primary"` default);
     * what it paints behind them is `before:bg-elevated`, a neutral plate. One class per item
     * turns that into `before:bg-primary/10` — appended AFTER the variant's own class, so
     * tailwind-merge keeps the later of the two `before:bg-*` utilities — and the row becomes
     * the tinted plate the shell uses everywhere else for "this one".
     *
     * `active` is set explicitly rather than left to the router, so a DETAIL route
     * (`/users/:id`) keeps its section lit.
     */
    const { t }                   = useI18n()
    const route                   = useRoute()
    const router                  = useRouter()
    const appStore                = useAppStore()
    const { isMobile, closeMenu, collapsed, toggleCollapsed } = useLayout()

    // FLEET: folded to icons — the desk only; the drawer is always the full column.
    const folded = computed((): boolean => !isMobile.value && collapsed.value)

    const alertsStore    = useAlertsStore()
    const remindersStore = useRemindersStore()

    /*
     * FLEET: the count behind an entry, or nothing at a count of nothing — a badge reading 0
     * is a badge that has to be read before it can be ignored. The open alerts and the
     * opomniki come from the stores the default layout starts; an API that predates either
     * leaves its store at zero, and the menu exactly as it was. Overdue / critical draws in
     * error, merely coming up in warning.
     */
    const badgeOf = (source: NavBadgeSource | undefined): { count: number, color: 'error' | 'warning' } | undefined => {
        if (source === 'alerts' && alertsStore.summary.open) {
            return { count: alertsStore.summary.open, color: alertsStore.badgeColour }
        }

        if (source === 'reminders' && remindersStore.badgeCount) {
            return { count: remindersStore.badgeCount, color: remindersStore.badgeColour }
        }

        return undefined
    }

    const groups = computed((): { labelKey: string | null, items: NavigationMenuItem[] }[] =>
        NAV_GROUPS.map((group) => ({
            labelKey: group.labelKey,
            items: group.items
                // FLEET: an entry a reader cannot use is left out, not greyed out.
                .filter((item) => !item.adminOnly || appStore.isAdmin())
                .map((item): NavigationMenuItem => {
                    const active = item.routes.includes(String(route.name ?? ''))
                    const badge  = badgeOf(item.badge)

                    // FLEET: folded, the tooltip is the only place the count can be said in
                    // words — and it is what a screen reader is given, because a coloured
                    // dot is not a sentence.
                    const label = badge && folded.value
                        ? `${t(item.labelKey)} (${badge.count})`
                        : t(item.labelKey)

                    return {
                        label,
                        icon:         item.icon,
                        to:           { name: item.name },
                        active,
                        class:        active ? 'before:bg-primary/10' : '',
                        'aria-label': label,
                        // FLEET: unfolded the count is a badge; folded there is no room for a
                        // number, so it is a dot on the icon.
                        badge:        badge && !folded.value
                            ? { label: String(badge.count), color: badge.color, variant: 'subtle', size: 'sm' }
                            : undefined,
                        chip:         badge && folded.value ? { color: badge.color } : undefined,
                        onSelect:     () => { closeMenu() },
                    }
                }),
        })).filter((group) => group.items.length))

    const userName = computed((): string => appStore.userData?.name || appStore.userData?.username || '')

    /** The second line of the user row: the account's role. */
    const userRole = computed((): string => appStore.userData ? roleLabel(appStore.userData.role) : '')

    const initials = computed((): string => userName.value
        .split(/[\s@._-]+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part.charAt(0).toUpperCase())
        .join(''))

    const userMenu = computed(() => [[{
        label:    t('general.sign_out'),
        icon:     'i-lucide-log-out',
        onSelect: () => {
            appStore.setLogout()
            void router.push({ name: 'login' })
        },
    }]])

    /*
     * FLEET: the version footer. GET /version is open and tiny. An API that predates it
     * answers 404, and then the line is simply not drawn — the console's own version is
     * known without asking.
     */
    const fetchStore = useFetchStore()

    const webVersion = __APP_VERSION__
    const api        = ref<IVersion>()

    const buildTitle = computed((): string => [__APP_COMMIT__, api.value?.commit].filter(Boolean).join(' · '))

    onMounted(async () => {
        const { statusCode, data } = await fetchStore.fetch('/version').get().json<IVersion>()

        if (statusCode.value === 200 && data.value?.version) api.value = data.value
    })
</script>
